import styled from 'styled-components';
import { key } from 'styled-theme';

/* =========================================================
   NAVBAR
   ========================================================= */

export const Nav = styled.nav`
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;

  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 20px 120px;

  background: ${({ isBackground, isQuestionScreen, isSpecificRoute }) =>
    isBackground
      ? 'linear-gradient(to right, #ECEAF8 50%, transparent 50%)'
      : isQuestionScreen
        ? 'linear-gradient(to right, #ECEAF8 50%, transparent 50%)'
        : isSpecificRoute
          ? '#ECEAF8'
          : 'transparent'};

  z-index: 999;

  box-sizing: border-box;

  /* =======================================================
     DESKTOP
     ======================================================= */

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    padding: 18px 64px;
  }

  /* =======================================================
     TABLET
     ======================================================= */

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 16px;
  }

  /* =======================================================
     MOBILE
     ======================================================= */

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 14px 16px;

    /*
      Important:
      Navbar itself remains transparent / same background.
      Only the menu becomes the white floating panel.
    */
  }
`;

/* =========================================================
   LOGO
   ========================================================= */

export const Logo = styled.div`
  cursor: pointer;

  display: flex;
  align-items: center;

  flex-shrink: 0;

  svg {
    display: block;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    transform: scale(0.95);
    transform-origin: left center;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    transform: scale(0.9);
    transform-origin: left center;
  }
`;

/* =========================================================
   DESKTOP ACTIONS
   ========================================================= */

export const DesktopActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  gap: 10px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none;
  }
`;

/* =========================================================
   COMMON BUTTON
   ========================================================= */

export const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;

  gap: 8px;

  padding: 12px 25px;

  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: 16px;

  border-radius: ${key('sizes.borderRadius.normal', '18px')};

  cursor: pointer;

  transition:
    opacity 0.2s ease,
    transform 0.2s ease;

  background: linear-gradient(90deg, #7b5cff, #60a5fa);

  color: white;

  border: 1px solid #eceaf8;

  white-space: nowrap;

  box-sizing: border-box;

  &:hover {
    opacity: 0.92;
  }

  &:active {
    transform: translateY(1px);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    padding: 11px 20px;
    font-size: 15px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-height: 50px;

    padding: 10px 17px;

    font-size: 15px;

    gap: 6px;

    border-radius: 15px;
  }
`;

/* =========================================================
   CREATE BUTTON
   ========================================================= */

export const CreateApplicationButton = styled(LogoutButton)`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-width: 160px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-width: 125px;
  }
`;

/* =========================================================
   MOBILE HAMBURGER
   ========================================================= */

export const MobileMenuButton = styled.button`
  display: none;

  width: 48px;
  height: 48px;

  padding: 0;

  border-radius: 10px;

  border: 1px solid #dddaf0;

  background: rgba(255, 255, 255, 0.55);

  align-items: center;
  justify-content: center;

  flex-direction: column;

  gap: 5px;

  cursor: pointer;

  transition:
    background 0.2s ease,
    border-color 0.2s ease;

  box-sizing: border-box;

  span {
    width: 21px;
    height: 2px;

    border-radius: 2px;

    background: #17163b;

    display: block;
  }

  &:hover {
    background: #ffffff;
    border-color: #cfcbea;
  }

  &:active {
    transform: scale(0.97);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: flex;
  }
`;

/* =========================================================
   MOBILE MENU
   ========================================================= */

export const MobileMenu = styled.div`
  position: absolute;

  top: calc(100% + -5px);

  left: 20px;
  right: 20px;

  background: #ffffff;

  border: 1px solid #e8e5f2;

  border-radius: 14px;

  box-shadow:
    0 12px 35px rgba(20, 18, 60, 0.12),
    0 3px 10px rgba(20, 18, 60, 0.05);

  overflow: hidden;

  box-sizing: border-box;

  animation: mobileMenuOpen 0.18s ease-out;

  @keyframes mobileMenuOpen {
    from {
      opacity: 0;
      transform: translateY(-6px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none;
  }
`;

/* =========================================================
   MOBILE CLOSE
   ========================================================= */

export const MobileMenuClose = styled.button`
  position: absolute;

  top: 10px;
  right: 12px;

  width: 40px;
  height: 40px;

  border: 0;

  background: transparent;

  color: #17163b;

  font-size: 30px;
  font-weight: 300;

  line-height: 1;

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;

  border-radius: 8px;

  &:hover {
    background: #f5f4fb;
  }
`;

/* =========================================================
   MOBILE MENU ITEM
   ========================================================= */

export const MobileMenuItem = styled.button`
  width: 100%;

  min-height: 64px;

  padding: 0 24px;

  display: flex;
  align-items: center;

  gap: 22px;

  border: 0;

  border-bottom: 1px solid #eceaf1;

  background: #ffffff;

  color: #17163b;

  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};

  font-size: 16px;

  text-align: left;

  cursor: pointer;

  box-sizing: border-box;

  &:last-child {
    border-bottom: 0;
  }

  svg {
    width: 22px;
    height: 22px;

    flex-shrink: 0;

    color: #34248f;
  }

  span {
    flex: 1;
  }

  &:hover {
    background: #faf9fd;
  }

  &:active {
    background: #f3f1fa;
  }
`;

/* =========================================================
   MOBILE LANGUAGE ITEM
   ========================================================= */

export const MobileLanguageItem = styled.div`
  width: 100%;

  min-height: 64px;

  padding: 0 24px;

  display: flex;
  align-items: center;

  gap: 22px;

  border-bottom: 1px solid #eceaf1;

  background: #ffffff;

  box-sizing: border-box;

  .language-icon {
    width: 22px;
    height: 22px;

    flex-shrink: 0;

    display: flex;
    align-items: center;
    justify-content: center;

    color: #34248f;

    font-size: 20px;
  }

  /*
    Let the existing DropDown occupy the remaining space.
  */

  > div {
    flex: 1;
    min-width: 0;
  }

  /*
    Prevent the existing dropdown from becoming
    wider than the mobile menu.
  */

  button {
    width: 100% !important;

    min-height: 44px;

    padding: 8px 0 !important;

    background: transparent !important;

    border: 0 !important;

    box-shadow: none !important;

    color: #17163b !important;

    text-align: left;

    font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};

    font-size: 16px;
  }
`;
